import type { ViewAsMode } from "@starsach/shared-types";

/**
 * ADR-0011: Gairm staff open a customer's portal in its own tab at
 * `/as/<organisation id>/…`. The prefix is the router's basename, so the
 * whole app runs unchanged underneath it, and it is what tells the HTTP
 * client to send the view-as headers. The server does the rest: it scopes
 * and refuses exactly as it would for the customer, and records any
 * change under the staff member's own name.
 *
 * The mode (read by default; write once the staff member asks) is kept
 * per tab and per organisation in sessionStorage, and changing it reloads
 * the tab so every query is re-asked with the new header.
 */
export interface ViewAsLocation {
  organisationId: string;
  basename: string;
}

const PREFIX = /^\/as\/([\w.-]{1,128})(?=\/|$)/;

export function viewAsFromLocation(pathname: string = window.location.pathname): ViewAsLocation | undefined {
  const m = PREFIX.exec(pathname);
  return m ? { organisationId: m[1]!, basename: `/as/${m[1]!}` } : undefined;
}

const modeKey = (organisationId: string) => `starsach-view-as-mode:${organisationId}`;

export function viewAsMode(organisationId: string): ViewAsMode {
  return window.sessionStorage.getItem(modeKey(organisationId)) === "write" ? "write" : "read";
}

export function setViewAsMode(organisationId: string, mode: ViewAsMode): void {
  if (mode === "read") window.sessionStorage.removeItem(modeKey(organisationId));
  else window.sessionStorage.setItem(modeKey(organisationId), mode);
}

/** The headers every request carries inside a view-as tab; none outside one. */
export function viewAsHeaders(): Record<string, string> {
  const v = viewAsFromLocation();
  if (!v) return {};
  return { "x-starsach-view-as": v.organisationId, "x-starsach-view-as-mode": viewAsMode(v.organisationId) };
}

/** Where "View their portal" opens: the customer's own page, inside their view. */
export function viewAsUrl(organisationId: string, path: string): string {
  return `/as/${encodeURIComponent(organisationId)}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * The session lives in sessionStorage, which is per tab. Chrome copies it
 * into a tab opened with an opener; Firefox and Safari do not, so a view-as
 * tab there arrived with no session, bounced to Cognito, and Cognito could
 * not return to a callback under `/as/…` - a loop (found on the first
 * Firefox walk, 2026-09-18). So the signed-in tab publishes its session:
 * every sessionStorage entry the auth layer owns is mirrored into one
 * localStorage key whenever the session changes, and a view-as tab takes
 * it when it opens. A first version wrote the mirror on the link's click;
 * Firefox navigated the new tab before that handler ran, so the mirror
 * follows the session instead and does not depend on the click at all.
 * localStorage is the only channel two tabs of one origin share
 * synchronously; the value is cleared on sign-out and is never read by a
 * tab outside `/as/…`.
 */
const HANDOFF_KEY = "starsach-view-as-handoff";
const HANDOFF_TTL_MS = 12 * 3_600_000;

function authEntries(): Record<string, string> {
  const entries: Record<string, string> = {};
  for (let i = 0; i < window.sessionStorage.length; i++) {
    const k = window.sessionStorage.key(i)!;
    if (k.startsWith("oidc.") || k === "mock-session" || k === "starsach.signedInAt") entries[k] = window.sessionStorage.getItem(k)!;
  }
  return entries;
}

/** Called by the signed-in tab whenever its session changes; a view-as tab never publishes (it holds a copy). */
export function handOffSession(): void {
  if (viewAsFromLocation()) return;
  const entries = authEntries();
  try {
    if (Object.keys(entries).length === 0) window.localStorage.removeItem(HANDOFF_KEY);
    else window.localStorage.setItem(HANDOFF_KEY, JSON.stringify({ at: Date.now(), entries }));
  } catch {
    // Storage unavailable: the view-as tab says it has no session, and the person opens it again from a signed-in tab.
  }
}

/** Forgets the published session - on sign-out, so a view-as tab opened afterwards does not inherit a dead one. */
export function clearHandedOffSession(): void {
  try {
    window.localStorage.removeItem(HANDOFF_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** Takes the published session, if this tab is a view-as tab with none of its own. Runs before React mounts. */
export function takeHandedOffSession(): void {
  if (!viewAsFromLocation()) return;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(HANDOFF_KEY);
  } catch {
    return;
  }
  if (!raw) return;
  try {
    const { at, entries } = JSON.parse(raw) as { at: number; entries: Record<string, string> };
    if (Date.now() - at > HANDOFF_TTL_MS) return;
    for (const [k, v] of Object.entries(entries)) if (!window.sessionStorage.getItem(k)) window.sessionStorage.setItem(k, v);
  } catch {
    // A malformed hand-off is ignored; the tab says it has no session.
  }
}
